import { useState } from 'react';
import Image from 'next/image';

// Libs
import { saveAs } from 'file-saver';
import Arrow from 'public/images/giveaway-picker/Arrow-Free-Tool.svg';
import PhoneIllustration from 'public/images/giveaway-picker/Iphone_Mockup_IGP.png';

import useTranslation from '@/utils/hooks/translation/useTranslation';

import { get } from '@/utils/client';

// Routes
import { GIVEAWAY_PICKER_RESULT } from '@/lib/routes/apiToolsEndpoints';

// Components
import Button from '@/components/base/Button';
import CompoundBanner from '@/components/base/InfoBanner/CompoundBanner';
// Types
import { GiveawayResult } from '@/components/pages/giveaway-picker/Types/drawTypes';
import { Trans } from '@/components/tools/translation';

export const DownloadImageBlock = ({ result }: { result: GiveawayResult }) => {
    const { t } = useTranslation('giveaway_picker');
    const [isLoading, setIsLoading] = useState(false);

    const ownerName = result.data.owner.name;

    const [error, setError] = useState(false);

    const handleDownload = async () => {
        setIsLoading(true);
        setError(false);
        try {
            const response = await get(
                `${GIVEAWAY_PICKER_RESULT}/${result.result.id}/download`,
                {
                    signature: result.download_signature
                },
                {
                    headers: {
                        'Content-Type': 'application/octet-stream'
                    }
                }
            );
            saveAs(response.data, `${ownerName}-giveaway-result.png`);
        } catch (error) {
            setError(true);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="flex flex-col gap-10 rounded-lg bg-icoBlue-100 px-10 lg:flex-row xl:min-h-96"
            data-testid="download-image-block">
            <div className="relative my-8 flex w-full flex-col items-start justify-center gap-8 text-left sm:my-14 lg:w-1/2">
                <div className="font-GBold text-3.5xl">
                    <Trans
                        i18nKey="giveaway_picker:results.story_title"
                        components={{ blue: <span className="text-icoBlue" /> }}
                    />
                </div>
                <div className="font-GMedium text-icoGray-500">
                    {t('results.story_description')}
                </div>
                {error ? (
                    <div className="mb-4">
                        <CompoundBanner>
                            <CompoundBanner.Icon />
                            <CompoundBanner.Content
                                message={t('results.error_downloading_image')}
                            />
                        </CompoundBanner>
                    </div>
                ) : null}
                <Button
                    onClick={handleDownload}
                    isLoading={isLoading}
                    data-testid="download-image-button">
                    {t('commons:download')}
                </Button>
                <span className="absolute -right-10 bottom-10 hidden rotate-12 xl:block">
                    <Image src={Arrow} alt="Arrow" width={120} />
                </span>
            </div>
            <div className="relative h-56 w-full overflow-hidden xs:h-64 sm:h-72 lg:h-auto lg:w-1/2">
                <Image
                    src={PhoneIllustration}
                    alt="Phone Illustration"
                    className="absolute top-0 xs:-top-4 sm:left-1/2 sm:w-1/2 sm:-translate-x-1/2 md:-top-5 md:w-full lg:-right-2 lg:top-6"
                />
            </div>
        </div>
    );
};
